import React from 'react'
import { Tooltip } from '@mui/material'
import {
  ArrowBack, ArrowForward, DarkMode as DarkModeIcon, LightMode as LightModeIcon, Search as SearchIcon,
  ContentCopy, VisibilityOutlined, VisibilityOffOutlined, OpenInNew, PublicOutlined,
} from '@mui/icons-material'
import { PrivatePage } from '../component/PagePrivate'
import { ViewToggle } from '../component/ViewToggle'
import { NotificationV2 } from '../container/common/NotificationV2'
import { ProfileV2 } from '../container/common/ProfileV2'
import { api } from '../function/axios'
import { formatDate } from '../function/date'
import { Subscriber } from '../store/user/reducer'
import { useAdminTheme } from '../hook/useAdminTheme'
import l from './SubscribersPageV2.module.css'
import s from '../component/apikey-v2/ApikeyV2.module.css'

/*
 * API keys in the v2 admin design. The classic page (ApikeyPage.tsx) is
 * untouched — including its developer-page video upload, which this page
 * leaves out. It reads the same list (/api-keys/admin), which the backend
 * pages 9 at most (limit < 10), so it is read page by page in full, then
 * searched, filtered and paged here.
 *
 * The key is hidden until shown; the key's secret, which the list also
 * carries, is never shown. A click on a row opens the owner's page.
 */

const PER_PAGE = 12
const SERVER_PAGE = 9

interface ApiKey {
  id: number
  environment: string
  key: string
  status?: string | null
  domains: string[] | null
  length: number
  user: Subscriber | null
  createdTime: string
}
type TypeFilter = 'all' | 'primary' | 'partner'
type DomainFilter = 'all' | 'restricted' | 'open'

async function loadAll(): Promise<ApiKey[]> {
  const out: ApiKey[] = []
  for (let offset = 0, guard = 0; guard < 500; guard += 1, offset += SERVER_PAGE) {
    // eslint-disable-next-line no-await-in-loop
    const { data } = await api({ method: 'GET', url: '/api-keys/admin', params: { offset, limit: SERVER_PAGE } })
    const list: ApiKey[] = Array.isArray(data) ? data : data?.list || []
    out.push(...list)
    const count = Array.isArray(data) ? null : Number(data?.count)
    if (list.length < SERVER_PAGE || (count != null && out.length >= count)) break
  }
  return out
}

const nameOf = (u: Subscriber | null) => (u ? [u.firstname, u.lastname].filter(Boolean).join(' ') || u.email || u.phone || `#${u.id}` : 'Deleted user')
const initials = (u: Subscriber | null) => (u ? `${(u.firstname || u.email || '?')[0]}${(u.lastname || '')[0] || ''}` : '?').toUpperCase()
const domainsOf = (k: ApiKey) => (Array.isArray(k.domains) ? k.domains.filter(Boolean) : [])
const typeOf = (k: ApiKey) => (k.status === 'partner' ? 'partner' : 'primary')
const mask = (key: string) => (key.length > 8 ? `${key.slice(0, 4)}••••••••${key.slice(-4)}` : '••••••••')

function Avatar({ u }: { u: Subscriber | null }) {
  const [broken, setBroken] = React.useState(false)
  return u?.avatar && !broken
    ? <img className={s.avatar} src={u.avatar} alt="" onError={() => setBroken(true)} />
    : <span className={s.avatar}>{initials(u)}</span>
}

function KeyCell({ value }: { value: string }) {
  const [shown, setShown] = React.useState(false)
  const [copied, setCopied] = React.useState(false)
  return (
    <div className={s.key} onClick={(e) => e.stopPropagation()}>
      <code>{shown ? value : mask(value)}</code>
      <Tooltip title={shown ? 'Hide' : 'Show the key'}>
        <button type="button" className={s.iconSm} onClick={() => setShown(!shown)} aria-label={shown ? 'Hide' : 'Show the key'}>
          {shown ? <VisibilityOffOutlined sx={{ fontSize: 15 }} /> : <VisibilityOutlined sx={{ fontSize: 15 }} />}
        </button>
      </Tooltip>
      <Tooltip title={copied ? 'Copied' : 'Copy the key'}>
        <button
          type="button"
          className={s.iconSm}
          onClick={() => { void navigator.clipboard?.writeText(value).catch(() => undefined); setCopied(true); window.setTimeout(() => setCopied(false), 1300) }}
          aria-label="Copy the key"
        >
          <ContentCopy sx={{ fontSize: 14 }} />
        </button>
      </Tooltip>
    </div>
  )
}

export function ApikeyPageV2() {
  const { theme, toggleTheme } = useAdminTheme()

  const [keys, setKeys] = React.useState<ApiKey[] | null>(null)
  const [query, setQuery] = React.useState('')
  const [type, setType] = React.useState<TypeFilter>('all')
  const [domain, setDomain] = React.useState<DomainFilter>('all')
  const [page, setPage] = React.useState(0)

  React.useEffect(() => {
    let alive = true
    loadAll().then((list) => { if (alive) setKeys(list) }).catch(() => { if (alive) setKeys([]) })
    return () => { alive = false }
  }, [])
  React.useEffect(() => { setPage(0) }, [query, type, domain])

  const all = keys || []
  const monthAgo = Date.now() - 30 * 86400000
  const owners = new Set(all.map((k) => k.user?.id).filter((x) => x != null)).size
  const counts = {
    primary: all.filter((k) => typeOf(k) === 'primary').length,
    partner: all.filter((k) => typeOf(k) === 'partner').length,
    restricted: all.filter((k) => domainsOf(k).length > 0).length,
    recent: all.filter((k) => new Date(k.createdTime).getTime() > monthAgo).length,
  }
  const keysPerOwner = all.reduce<Record<number, number>>((m, k) => {
    if (k.user?.id != null) m[k.user.id] = (m[k.user.id] || 0) + 1
    return m
  }, {})

  const q = query.trim().toLowerCase()
  const list = all
    .filter((k) => type === 'all' || typeOf(k) === type)
    .filter((k) => domain === 'all' || (domain === 'restricted' ? domainsOf(k).length > 0 : domainsOf(k).length === 0))
    .filter((k) => !q || [nameOf(k.user), k.user?.email, k.user?.uid, k.key, ...domainsOf(k)].some((v) => (v || '').toLowerCase().includes(q)))
  const pages = Math.max(1, Math.ceil(list.length / PER_PAGE))
  const shown = list.slice(page * PER_PAGE, page * PER_PAGE + PER_PAGE)

  const open = (k: ApiKey) => { if (k.user) window.open(`/users/subscriber/${k.user.id}`, '_blank', 'noopener,noreferrer') }

  const header = (
    <div className={`${l.theme} ${l.header}`} data-theme={theme}>
      <div className={l.crumb}><small>Developers</small><b>API keys</b></div>
      <div className={l.spacer} />
      <label className={l.search}>
        <SearchIcon sx={{ fontSize: 17 }} />
        <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search owner, key, domain" />
      </label>
      <ViewToggle dark={theme === 'dark'} />
      <Tooltip title={theme === 'dark' ? 'Light theme' : 'Dark theme'}>
        <button className={l.iconBtn} onClick={toggleTheme} aria-label="Toggle theme">
          {theme === 'dark' ? <LightModeIcon sx={{ fontSize: 17 }} /> : <DarkModeIcon sx={{ fontSize: 17 }} />}
        </button>
      </Tooltip>
      <NotificationV2 />
      <ProfileV2 />
    </div>
  )

  return (
    <PrivatePage header={header}>
      <div className={`${l.theme} ${l.page}`} data-theme={theme}>
        <div className={`${l.kpis} ${s.kpis}`}>
          <div className={l.kpi}><small>API keys</small><b>{keys ? all.length : '—'}</b><span>{keys ? `${counts.recent} new in 30 days` : ''}</span></div>
          <div className={l.kpi}><small>Owners</small><b>{keys ? owners : '—'}</b><span>users with a key</span></div>
          <div className={l.kpi}><small>Partner keys</small><b>{keys ? counts.partner : '—'}</b><span>{keys ? `${counts.primary} primary` : ''}</span></div>
          <div className={l.kpi}><small>Domain-restricted</small><b>{keys ? counts.restricted : '—'}</b><span>{keys ? `${all.length - counts.restricted} work from any domain` : ''}</span></div>
        </div>

        <div className={l.sec}>
          <div className={s.chips}>
            {([['all', 'All', all.length], ['primary', 'Primary', counts.primary], ['partner', 'Partner', counts.partner]] as const).map(([k, label, n]) => (
              <button key={k} type="button" className={`${l.chip} ${type === k ? l.chipOn : ''}`} onClick={() => setType(k)}>
                {label}{keys ? <span className={s.chipCount}>{n}</span> : null}
              </button>
            ))}
          </div>
          <span className={s.sep} />
          <div className={s.chips}>
            {([['all', 'Any domain setting'], ['restricted', 'Restricted to domains'], ['open', 'Any domain']] as const).map(([k, label]) => (
              <button key={k} type="button" className={`${l.chip} ${domain === k ? l.chipOn : ''}`} onClick={() => setDomain(k)}>{label}</button>
            ))}
          </div>
        </div>

        <div className={l.card}>
          <div className={l.scroll}>
            {keys === null ? (
              <div className={l.empty}>Loading…</div>
            ) : !shown.length ? (
              <div className={l.empty}>{q || type !== 'all' || domain !== 'all' ? 'Nothing matches the filter' : 'No API keys yet'}</div>
            ) : (
              <table className={l.table}>
                <thead>
                  <tr>
                    <th>Owner</th>
                    <th>Key</th>
                    <th>Type</th>
                    <th>Allowed domains</th>
                    <th>Length</th>
                    <th>Created</th>
                    <th aria-label="Open" />
                  </tr>
                </thead>
                <tbody>
                  {shown.map((k) => {
                    const doms = domainsOf(k)
                    const more = k.user?.id != null ? (keysPerOwner[k.user.id] || 1) - 1 : 0
                    return (
                      <tr key={k.id} onClick={() => open(k)} title={k.user ? 'Open the owner’s page' : undefined}>
                        <td>
                          <div className={s.person}>
                            <Avatar u={k.user} />
                            <div className={s.who}>
                              <b>{nameOf(k.user)}</b>
                              <span>{k.user?.email || k.user?.phone || ''}{more > 0 ? ` · ${more + 1} keys` : ''}</span>
                            </div>
                          </div>
                        </td>
                        <td><KeyCell value={k.key} /></td>
                        <td className={l.nowrap}>
                          <span className={`${s.badge} ${typeOf(k) === 'partner' ? s.badgePartner : ''}`}>{typeOf(k) === 'partner' ? 'Partner' : 'Primary'}</span>
                          {k.environment && <span className={l.sub}>{k.environment}</span>}
                        </td>
                        <td>
                          {doms.length ? (
                            <div className={s.domains}>
                              {doms.slice(0, 3).map((d) => <span key={d} className={s.tag}>{d}</span>)}
                              {doms.length > 3 && <Tooltip title={doms.slice(3).join(', ')}><span className={s.tag}>+{doms.length - 3}</span></Tooltip>}
                            </div>
                          ) : <span className={s.anyDomain}><PublicOutlined sx={{ fontSize: 14 }} />Any domain</span>}
                        </td>
                        <td>{k.length || '—'}</td>
                        <td className={l.nowrap}>{formatDate(k.createdTime, 'dd.MM.yyyy')}<span className={l.sub}>{formatDate(k.createdTime, 'HH:mm')}</span></td>
                        <td onClick={(e) => e.stopPropagation()}>
                          {k.user && (
                            <Tooltip title="Open the owner’s page">
                              <button type="button" className={s.act} onClick={() => open(k)} aria-label="Open the owner’s page"><OpenInNew sx={{ fontSize: 15 }} /></button>
                            </Tooltip>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            )}
          </div>
          {pages > 1 && (
            <div className={s.pager}>
              <span>{page * PER_PAGE + 1}–{Math.min(list.length, (page + 1) * PER_PAGE)} of {list.length}</span>
              <div className={s.pagerBtns}>
                <button type="button" className={s.act} disabled={page === 0} onClick={() => setPage(page - 1)} aria-label="Previous page"><ArrowBack sx={{ fontSize: 16 }} /></button>
                <button type="button" className={s.act} disabled={page >= pages - 1} onClick={() => setPage(page + 1)} aria-label="Next page"><ArrowForward sx={{ fontSize: 16 }} /></button>
              </div>
            </div>
          )}
        </div>
      </div>
    </PrivatePage>
  )
}
